import { useStore } from 'hooks/useStore';
import { useState } from 'react';
import { JobPasteServices } from 'services/jobPasteServices';
import { handleApiError } from 'utils/handleApiError';

export const useNoteTemplate = (jobId: string) => {
  const { JobPasteStore } = useStore();
  const { isEditMode } = JobPasteStore;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const addNote = async (noteText: string, createdBy: string) => {
    if (!noteText.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        JobSchedule: jobId,
        note_text: noteText,
        created_by: createdBy
      };

      const response = await JobPasteServices.addJobNote(payload);

      // Optimistically update the UI without waiting for a refresh
      JobPasteStore.addNoteToJob(jobId, {
        id: response.id,
        noteText: response.note_text,
        createdBy: response.created_by,
        created_at: response.created_at,
        updated_at: response.updated_at
      });

      return response;
    } catch (error) {
      handleApiError(error, 'Failed to add note');
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateNote = async (noteId: number, noteText: string, createdBy: string) => {
    if (!noteText.trim()) {
      return;
    }

    setIsUpdating(true);
    try {
      const payload = {
        note_text: noteText,
        created_by: createdBy
      };

      const response = await JobPasteServices.updateJobNote(noteId, payload);

      // Optimistically update the UI
      JobPasteStore.updateNoteInJob(jobId, noteId, {
        id: response.id,
        noteText: response.note_text,
        createdBy: response.created_by,
        created_at: response.created_at,
        updated_at: response.updated_at
      });

      return response;
    } catch (error) {
      handleApiError(error, 'Failed to update note');
      return null;
    } finally {
      setIsUpdating(false);
    }
  };

  const getNotes = async () => {
    try {
      const response = await JobPasteServices.getJobNotes(jobId);
      return response.notes;
    } catch (error) {
      handleApiError(error, 'Failed to fetch notes');
      return [];
    }
  };

  return {
    addNote,
    updateNote,
    getNotes,
    isSubmitting,
    isUpdating,
    isEditMode
  };
};
